import {keyframes, styled} from '@mui/material/styles'
import {Backdrop, Box, Button, IconButton, Paper, Stack, TextField, Typography} from '@mui/material'
import theme from '../../../../../themes/index'
import {StyledFilledInput, StyledFormControl} from '../../../../Common/SearchPanel/Styles'
import {SendOutlined} from '@mui/icons-material'
import {StyledButton} from '../../Styles'

export const StyledPopupDialog = styled(Typography)(({theme}) => ({
  fontSize: theme.typography.body.fontSize.xs,
  color: theme.palette.common.black
}))

export const StyledDiv = styled('div')(({theme}) => ({
  display: 'flex',
  padding: '10px'
}))

export const StyledChatTypography = styled(Typography)(({theme}) => ({
  fontSize: theme.typography.body.fontSize.xs,
  whiteSpace: 'pre-wrap'
}))

export const StyledBackdrop = styled(Backdrop)(({theme}) => ({
  zIndex: 9999,
  backgroundColor: theme.palette.common.black5
}))

export const StyledPaperContainer = styled(Paper, {shouldForwardProp: (prop) => prop !== 'editClicked'})(
  ({theme, editClicked}) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    padding: '10px',
    zIndex: editClicked && 99999,
    border: editClicked && `2px solid ${theme.palette.common.white}`
  })
)

export const DivContainer = styled('div', {shouldForwardProp: (prop) => prop !== 'editClicked'})(({editClicked}) => ({
  display: 'flex',
  flexGrow: 1,
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  zIndex: editClicked && 99999
}))

export const StyledPromptTypography = styled(Typography)(() => ({
  fontSize: theme.typography.body.fontSize.xs,
  flexGrow: 1,
  wordBreak: 'break-word'
}))

export const PromptContainer = styled(Typography, {shouldForwardProp: (prop) => prop !== 'editClicked'})(
  ({theme, editClicked}) => ({
    color: theme.palette.common.green,
    fontSize: theme.typography.body.fontSize.xs,
    zIndex: editClicked && 99999
  })
)

export const StyledTextField = styled(Typography)(() => ({
  flexGrow: 1,
  marginRight: '10px'
}))

export const StyledIconButton = styled(IconButton)(({theme}) => ({
  '&.Mui-disabled': {
    color: theme.palette.common.grey
  }
}))

export const StyledTextTypography = styled(Typography)(() => ({
  fontSize: '14px'
}))

export const StyledButtonResponse = styled(Button)(() => ({
  float: 'right',
  marginRight: '10px'
}))

export const StyledResponseDivContainer = styled('div')(() => ({
  padding: '10px',
  float: 'right',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end'
}))

export const BouncingLoaderContainer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',
  margin: '25px 6px 6px 10px'
})

export const BouncingLoaderDiv = styled('div')(({theme}) => ({
  width: '10px',
  height: '10px',
  margin: '2px',
  background: theme.palette.common.grey2,
  borderRadius: '50%',
  animation: `${bouncingLoader} 0.5s infinite alternate`,
  '&:nth-of-type(2)': {
    animationDelay: '0.15s'
  },
  '&:nth-of-type(3)': {
    animationDelay: '0.3s'
  }
}))

const bouncingLoader = keyframes`
  to {
    opacity: 0.4;
    transform: translate3d(0, -14px, 0);
  }
`

export const StyledInput = styled(StyledFilledInput)(({theme}) => ({
  backgroundColor: theme.palette.common.grey5,
  '&.Mui-focused': {
    backgroundColor: theme.palette.common.grey5,
    color: theme.palette.greyShad
  },
  '&:hover': {
    backgroundColor: theme.palette.common.grey5,
    color: theme.palette.greyShad
  },
  '&.MuiFilledInput-root': {
    paddingTop: '0'
  },
  '& .MuiInputBase-input::placeholder': {
    color: theme.palette.common.grey6
  }
}))

export const StyledSendOutlined = styled(SendOutlined)(({theme}) => ({
  color: theme.palette.common.darkGrey
}))

export const StyledChatMessageBox = styled(Box)(() => ({
  marginTop: '10px',
  marginBottom: '10px'
}))

export const StyledStack = styled(Stack)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px'
}))

export const StyledBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'sender'
})(({sender}) => ({
  display: 'flex',
  justifyContent: sender === 'bot' ? 'flex-start' : 'flex-end'
}))

export const StyledPaper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'sender'
})(({sender}) => ({
  padding: '10px',
  backgroundColor: sender === 'bot' ? 'transparent' : theme.palette.blackShade,
  width: 'fit-content',
  wordBreak: 'break-word'
}))

export const StyledImg = styled('img')(() => ({
  alignSelf: 'flex-start',
  marginTop: '4px'
}))

export const StyledChatTextField = styled(TextField)(() => ({
  flexGrow: 1,
  marginRight: '10px',
  input: {
    color: theme.palette.common.white,
    fontSize: theme.typography.body.fontSize.xs,
    paddingTop: '4px'
  }
}))

export const StyledFormControlChatInput = styled(StyledFormControl)(() => ({
  bottom: '0',
  position: 'sticky'
}))

export const StyledDivInput = styled('div')(() => ({
  position: 'sticky',
  bottom: 0,
  marginTop: '10px'
}))

export const StyledDisclaimerPaper = styled(Paper)(() => ({
  padding: '5px'
}))

export const StyledDivPaper = styled('div')(() => ({
  display: 'flex',
  color: theme.palette.common.white
}))

export const StyledDiscTypography = styled(Typography)(() => ({
  fontSize: '12px',
  marginLeft: '5px',
  fontStyle: 'italic'
}))

export const StyledNewChatContainer = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginRight: '10px'
}))

export const StyledNewChatButton = styled(StyledButton)(({theme}) => ({
  border: `1px solid ${theme.palette.common.white}`,
  '&.Mui-disabled': {
    border: `1px solid ${theme.palette.common.grey}`,
    color: theme.palette.common.grey
  }
}))

export const StyledContainer = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginBottom: '10px'
}))

export const StyledExportDiv = styled('div')(({theme}) => ({
  display: 'flex',
  width: 'fit-content',
  border: `1px solid ${theme.palette.common.white}`,
  borderRadius: '32px',
  padding: '10px',
  backgroundColor: theme.palette.common.black3
}))
